import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { Ability } from 'ember-can';
import type AbilitiesService from 'ember-can/services/abilities';
import type { GetCanReviewOrReadBlocklistMatchModel } from 'garaje/abilities/security';
import type GroupInviteModel from 'garaje/models/group-invite';
import InviteModel from 'garaje/models/invite';
import type AuthzService from 'garaje/services/authz';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import { canTriggerWorkflowReeval } from 'garaje/utils/invite-workflow-reeval';
import { GLOBAL_ADMIN, ZONE_ADMIN } from 'garaje/utils/roles';
import { Permission } from 'garaje/utils/ui-permissions';
import _intersection from 'lodash/intersection';

const CAN_DELETE_WALKUP_ROLES = [GLOBAL_ADMIN, ZONE_ADMIN];

// Any of these permissions are sufficient to enable Settings > Invites in the left nav
const CAN_VISIT_INVITE_SETTINGS_AUTHZ_PERMISSIONS = [
  Permission.VISITORS_LOCATION_CONFIG_UPDATE_INVITES,
  Permission.VISITORS_LOCATION_CONFIG_UPDATE_SECURITY_DESK_LINK,
] as const;

export default class InviteAbility extends Ability {
  @service declare abilities: AbilitiesService;
  @service declare authz: AuthzService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare state: StateService;

  declare model: InviteModel | GroupInviteModel | null;

  get canDelete(): boolean {
    const invite = this.model;
    if (invite) {
      if (this.#isInviteDeniedAndUneditable) return false;

      if (this.#isInviteCreator || this.#isInviteHost || this.#isInviteAssistantOfHost) {
        return true;
      }
    }

    return this.authz.hasPermissionAtCurrentLocation(Permission.VISITORS_INVITE_DELETE);
  }

  get canEdit(): boolean {
    const invite = this.model;
    if (invite) {
      if (this.#isInviteDeniedAndUneditable) return false;

      if (this.#isInviteCreator || this.#isInviteHost || this.#isInviteAssistantOfHost) {
        return true;
      }
    }

    return this.authz.hasPermissionAtCurrentLocation(Permission.VISITORS_INVITE_UPDATE);
  }

  // Determine if the current user may be able to review any Invite record at all
  get canReview(): boolean {
    const blocklistContacts = this.state.currentLocation?.blocklistContacts?.content ?? [];
    const isSecurityContact = !!blocklistContacts.find((contact) => contact.id === this.currentAdmin.id);

    return this.canViewAll || isSecurityContact;
  }

  get canDeleteConnectWalkup(): boolean {
    const { roleNames } = this.currentAdmin;
    return isPresent(_intersection(CAN_DELETE_WALKUP_ROLES, roleNames));
  }

  get canViewAll(): boolean {
    return this.authz.hasPermissionAtCurrentLocation(Permission.VISITORS_INVITE_READ);
  }

  get canConfigure(): boolean {
    const permissions = [...CAN_VISIT_INVITE_SETTINGS_AUTHZ_PERMISSIONS];

    return this.authz.hasAnyPermissionAtCurrentLocation(permissions);
  }

  get canEnable(): boolean {
    return this.authz.hasPermissionAtCurrentLocation(Permission.VISITORS_LOCATION_CONFIG_UPDATE_INVITES);
  }

  /** True when the user created or hosts this invite (or assists its host), not merely permitted at its location. */
  get canUploadDocuments(): boolean {
    if (!this.featureFlags.isEnabled('visitors-host-prereg-upload')) return false;
    if (!this.model) return false;

    return this.#isInviteCreator || this.#isInviteHost || this.#isInviteAssistantOfHost;
  }

  get #isInviteAssistantOfHost(): boolean {
    // if not given an invite, don't bother checking invite-specific things
    if (!this.model) return false;
    if (!this.currentAdmin.employee) return false;

    const bossIds = this.currentAdmin.employee.hasMany('bosses').ids();

    return isPresent(_intersection(bossIds, this.#inviteHostIds));
  }

  get #isInviteCreator(): boolean {
    const invite = this.model;
    if (!invite || invite.isDestroying) return false;
    if (!this.currentAdmin.user) return false;

    const creatorId = invite.belongsTo('creator').id();
    const currentUserId = this.currentAdmin.user.id;

    return !!creatorId && creatorId === currentUserId;
  }

  get #isInviteHost(): boolean {
    const currentEmployeeId = this.currentAdmin.employee?.id;
    if (!currentEmployeeId) return false;

    return this.#inviteHostIds.includes(currentEmployeeId);
  }

  get #inviteHostIds(): string[] {
    const invite = this.model;
    if (!invite || invite.isDestroying) return [];

    const hostIds: string[] = [];
    const primaryHostId = invite.belongsTo('employee').id();
    if (primaryHostId) {
      hostIds.push(primaryHostId);
    }

    if (this.state.features?.canAccessMultipleHosts) {
      // multiple hosts suppport is enabled; combine primary host & additional hosts
      hostIds.push(...invite.hasMany('additionalHosts').ids());
    }
    return hostIds;
  }

  get #isInviteDeniedAndUneditable(): boolean {
    const invite = this.model;

    if (invite?.isDestroying) return false;
    if (!invite || invite instanceof InviteModel === false || !invite.belongsTo('visitApproval').id()) return false;

    const model: GetCanReviewOrReadBlocklistMatchModel = {
      visitApproval: invite.visitApproval,
      context: 'location',
      parent: invite,
    };

    const cannotReview = this.abilities.cannot('review entry-approval', model);

    // Hosts/creators/assistants can still edit a denied invite when saving will trigger
    // a workflow re-evaluation.
    return cannotReview && invite.approvalWasDenied && !canTriggerWorkflowReeval(invite);
  }
}
